import { createContext, ReactNode, useContext, useEffect, useRef } from 'react';
import { useQuery, useMutation, UseMutationResult } from '@tanstack/react-query';
import { insertUserSchema, User as SelectUser, InsertUser } from '@shared/schema';
import { getQueryFn, apiRequest, queryClient } from '../lib/queryClient';
import { useToast } from '@/hooks/use-toast';
import { useLocation } from 'wouter';
import {
  generateSharedAuthToken,
  verifySharedAuthToken,
  storeAuthToken,
  retrieveAuthToken,
  clearAuthToken,
  UserWithoutPassword,
} from '../lib/auth-sharing';
import { authBroadcaster } from '../lib/auth-broadcast';

// Simple registration data type for our form
type RegisterData = {
  username: string;
  password: string;
};

type AuthContextType = {
  user: SelectUser | null;
  isLoading: boolean;
  error: Error | null;
  loginMutation: UseMutationResult<SelectUser, Error, LoginData>;
  logoutMutation: UseMutationResult<void, Error, void>;
  registerMutation: UseMutationResult<any, Error, RegisterData>;
  getAuthToken: () => Promise<string | null>;
  loginWithToken: (token: string) => Promise<SelectUser | null>;
  requestPasswordResetMutation: UseMutationResult<{ message: string }, Error, { username: string }>;
  resetPasswordMutation: UseMutationResult<
    { message: string },
    Error,
    { token: string; newPassword: string }
  >;
  resendVerificationMutation: UseMutationResult<{ message: string }, Error, { email: string }>;
};

type LoginData = {
  username: string;
  password: string;
};

export const AuthContext = createContext<AuthContextType | null>(null);
const HEARTBEAT_INTERVAL = 5 * 60 * 1000; // 5 minutes
const REFRESH_THRESHOLD = 30 * 60 * 1000; // Refresh when less than 30 minutes remaining
const WARNING_THRESHOLD = 5 * 60 * 1000; // Warn when less than 5 minutes remaining

export function AuthProvider({ children }: { children: ReactNode }) {
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const warningShownRef = useRef(false);

  const {
    data: user,
    error,
    isLoading,
    refetch,
  } = useQuery<SelectUser | undefined, Error>({
    queryKey: ['/api/user'],
    queryFn: getQueryFn({ on401: 'returnNull' }),
    staleTime: 30 * 60 * 1000, // Consider data fresh for 30 minutes
    gcTime: 60 * 60 * 1000, // Keep in cache for 1 hour
    refetchOnWindowFocus: false, // Don't refetch on window focus
    refetchOnMount: false, // Don't always refetch on mount to prevent constant reauth
    refetchInterval: false, // Disable automatic background refetching
    retry: (failureCount, error) => {
      // Don't retry on 401 errors
      if (error && 'status' in error && error.status === 401) {
        return false;
      }
      return failureCount < 2; // Reduce retry attempts
    },
  });

  // Check if there's a shared auth token in localStorage on mount
  useEffect(() => {
    // Only run auto-auth once when the component mounts and user is not loaded
    if (!user && !isLoading) {
      // First try to use a stored token
      const storedToken = retrieveAuthToken();
      if (storedToken) {
        loginWithToken(storedToken)
          .then((authUser) => {
            if (!authUser) {
              // Clear invalid token
              clearAuthToken();

              // Only try dev-login in true development environments
              const isDevelopment =
                import.meta.env.DEV &&
                (window.location.hostname === 'localhost' ||
                  window.location.hostname === '127.0.0.1');

              if (isDevelopment) {
                tryDevLogin();
              }
            }
          })
          .catch((error) => {
            console.error('Error logging in with stored token:', error);
            clearAuthToken();

            // Only try dev-login in true development environments
            const isDevelopment =
              import.meta.env.DEV &&
              (window.location.hostname === 'localhost' ||
                window.location.hostname === '127.0.0.1');

            if (isDevelopment) {
              tryDevLogin();
            }
          });
      } else {
        // Only try dev login in true development environments (not on deployed Replit apps)
        const isDevelopment =
          import.meta.env.DEV &&
          (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

        if (isDevelopment) {
          tryDevLogin();
        }
      }
    }
  }, [user, isLoading]); // Add dependencies to prevent infinite loops

  // Auto-login helper function
  async function tryDevLogin() {
    try {
      console.log('Attempting auto-login...');
      const response = await fetch('/api/dev-login', {
        method: 'GET',
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Auto-login successful:', data);

        // Update the user in the query cache immediately
        queryClient.setQueryData(['/api/user'], data.user);

        // Invalidate all queries to refresh data with authenticated context
        await queryClient.invalidateQueries();

        return data.user;
      } else {
        console.error('Auto-login failed:', await response.text());
        return null;
      }
    } catch (error) {
      console.error('Error during auto-login:', error);
      return null;
    }
  }

  // Function to generate and return an auth token for cross-app authentication
  async function getAuthToken(): Promise<string | null> {
    if (!user) {
      toast({
        title: 'Authentication required',
        description: 'You must be logged in to generate an auth token',
        variant: 'destructive',
      });
      return null;
    }

    const token = await generateSharedAuthToken();
    if (token) {
      storeAuthToken(token);
    }
    return token;
  }

  // Function to login with a token from another application
  async function loginWithToken(token: string): Promise<SelectUser | null> {
    try {
      const verifiedUser = await verifySharedAuthToken(token);
      if (verifiedUser) {
        // Store the successful token
        storeAuthToken(token);

        // Now that we've verified the token, we need to establish a server session
        // We'll do this by calling the login-with-token endpoint
        try {
          // Create a server session by calling a dedicated endpoint
          const user = await apiRequest<SelectUser>('POST', '/api/login-with-token', { token });

          // Update the user in the query cache
          queryClient.setQueryData(['/api/user'], user);

          toast({
            title: 'Authentication successful',
            description: `Welcome back, ${user.username}!`,
          });

          // Refresh data to ensure we have correct authentication state
          refetch();

          // Redirect to dashboard after token-based authentication
          navigate('/');

          return user;
        } catch (err) {
          console.error('Failed to establish session with token:', err);
          clearAuthToken();
          toast({
            title: 'Session creation failed',
            description: 'Could not establish a session with your authentication token',
            variant: 'destructive',
          });
          return null;
        }
      }
      return null;
    } catch (error) {
      console.error('Failed to login with token:', error);
      toast({
        title: 'Authentication failed',
        description: 'The provided authentication token is invalid',
        variant: 'destructive',
      });
      return null;
    }
  }

  const loginMutation = useMutation({
    mutationFn: async (credentials: LoginData) => {
      try {
        const response = await apiRequest<SelectUser>('POST', '/api/login', credentials);
        return response;
      } catch (error: any) {
        // Check if it's an email verification error
        if (error.status === 403 && error.requiresVerification) {
          throw {
            ...error,
            isVerificationError: true,
            email: error.email,
          };
        }
        throw error;
      }
    },
    onSuccess: async (user: SelectUser) => {
      // Update the user in the query cache immediately
      queryClient.setQueryData(['/api/user'], user);

      // Invalidate all queries to refresh data with authenticated context
      await queryClient.invalidateQueries();

      // Generate and store an auth token after successful login
      getAuthToken().catch(console.error);

      // Broadcast login event to other tabs
      authBroadcaster.broadcast({ type: 'login', user });

      toast({
        title: 'Login successful',
        description: `Welcome back, ${user.username}!`,
      });

      // Redirect to dashboard after successful login
      navigate('/');
    },
    onError: (error: any) => {
      // Don't show toast for verification errors - let the component handle it
      if (!error.isVerificationError) {
        toast({
          title: 'Login failed',
          description: error.message,
          variant: 'destructive',
        });
      }
    },
  });

  const registerMutation = useMutation({
    mutationFn: async (credentials: RegisterData) => {
      // Send the registration request with the standard format expected by the server
      const response = await apiRequest<any>('POST', '/api/register', {
        username: credentials.username,
        password: credentials.password, // The server will hash this
      });
      return response;
    },
    onSuccess: (response: any) => {
      // Registration now returns verification info instead of immediately logging in
      if (response.emailSent) {
        toast({
          title: 'Registration successful!',
          description: response.message,
        });
      } else {
        toast({
          title: 'Registration successful',
          description: response.message,
          variant: 'destructive',
        });
      }
    },
    onError: (error: Error) => {
      toast({
        title: 'Registration failed',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const logoutMutation = useMutation({
    mutationFn: async () => {
      console.log('Logout: Initiating logout request');
      try {
        const response = await fetch('/api/logout', {
          method: 'POST',
          credentials: 'include',
        });

        console.log(`Logout: Server response status: ${response.status}`);

        // Force update the query cache regardless of response
        queryClient.setQueryData(['/api/user'], null);
        queryClient.invalidateQueries({ queryKey: ['/api/user'] });

        // Clear the stored auth token
        clearAuthToken();

        // Return success even if we don't get a response body
        return;
      } catch (err) {
        console.error('Logout error:', err);
        throw err;
      }
    },
    onSuccess: () => {
      console.log('Logout: onSuccess handler running');

      // Update query cache and force a refetch on next access
      queryClient.resetQueries({ queryKey: ['/api/user'] });

      // Broadcast logout event to other tabs
      authBroadcaster.broadcast({ type: 'logout' });

      // Show success message
      toast({
        title: 'Logged out',
        description: 'You have been successfully logged out',
      });

      // Redirect to login page with timeout to ensure state updates first
      setTimeout(() => {
        navigate('/auth');
      }, 100);
    },
    onError: (error: Error) => {
      console.error('Logout error in onError handler:', error);
      toast({
        title: 'Logout failed',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Password reset mutation - Step 1: Request reset email
  const requestPasswordResetMutation = useMutation({
    mutationFn: async ({ username }: { username: string }) => {
      return await apiRequest<{ message: string }>('POST', '/api/request-password-reset', {
        username,
      });
    },
    onSuccess: (response) => {
      toast({
        title: 'Password Reset Email Sent',
        description: response.message,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Password Reset Request Failed',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Password reset mutation - Step 2: Reset password with token
  const resetPasswordMutation = useMutation({
    mutationFn: async ({ token, newPassword }: { token: string; newPassword: string }) => {
      return await apiRequest<{ message: string }>('POST', '/api/reset-password', {
        token,
        newPassword,
      });
    },
    onSuccess: (response) => {
      // Don't show toast here - the AuthPage will handle the success state
      // Just return the response for the component to handle
      return response;
    },
    onError: (error: Error) => {
      toast({
        title: 'Password Reset Failed',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Email verification resend mutation
  const resendVerificationMutation = useMutation({
    mutationFn: async ({ email }: { email: string }) => {
      return await apiRequest<{ message: string }>('POST', '/api/resend-verification', { email });
    },
    onSuccess: (response) => {
      toast({
        title: 'Verification Email Sent',
        description: response.message,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to Send Verification Email',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Session heartbeat effect
  useEffect(() => {
    if (!user) {
      warningShownRef.current = false;
      return;
    }

    let intervalId: NodeJS.Timeout;

    const sendHeartbeat = async () => {
      try {
        const response = await apiRequest<{
          success: boolean;
          remainingTime: number;
          expiresAt: string;
        }>('POST', '/api/session-heartbeat');

        if (response.success) {
          const remainingTime = response.remainingTime;

          // Check if we need to refresh the session
          if (remainingTime < REFRESH_THRESHOLD) {
            console.log('[Session] Refreshing session before expiry');
            await apiRequest('POST', '/api/session-refresh');
            warningShownRef.current = false; // Reset warning flag after refresh
            // Broadcast session refresh to other tabs
            authBroadcaster.broadcast({ type: 'session-refresh' });
          }

          // Warn user if session is about to expire
          else if (remainingTime < WARNING_THRESHOLD && !warningShownRef.current) {
            warningShownRef.current = true;
            toast({
              title: 'Session Expiring Soon',
              description: 'Your session will expire in less than 5 minutes. Save your work.',
              variant: 'destructive',
            });
          }
        }
      } catch (error: any) {
        const errorMsg = error?.message || '';
        const is401 = errorMsg.includes('401');
        const is503 = errorMsg.includes('503');

        // Only log actual errors, not 503s (app not running)
        if (!is503) {
          console.error('[Session] Heartbeat failed:', error);
        }

        // If heartbeat fails with 401, user is no longer authenticated
        if (is401) {
          toast({
            title: 'Session Expired',
            description: 'Your session has expired. Please log in again.',
            variant: 'destructive',
          });

          // Force logout
          logoutMutation.mutate();
        }
        // Silently ignore 503 errors (app temporarily not running)
        else if (is503) {
          return;
        }
      }
    };

    // Send initial heartbeat
    sendHeartbeat();

    // Set up interval
    intervalId = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL);

    // Also send heartbeat on visibility change
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        sendHeartbeat();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup
    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user, logoutMutation, toast]);

  // Listen for auth events from other tabs
  useEffect(() => {
    const unsubscribeLogin = authBroadcaster.subscribe('login', (event) => {
      console.log('[Auth] Login event received from another tab');
      // Update local state with the logged-in user
      queryClient.setQueryData(['/api/user'], event.user);
      refetch();
    });

    const unsubscribeLogout = authBroadcaster.subscribe('logout', () => {
      console.log('[Auth] Logout event received from another tab');
      // Clear local state
      queryClient.setQueryData(['/api/user'], null);
      clearAuthToken();
      // Redirect to login page
      navigate('/auth');
    });

    const unsubscribeSessionRefresh = authBroadcaster.subscribe('session-refresh', () => {
      console.log('[Auth] Session refresh event received from another tab');
      // Refetch user data to get updated session info
      refetch();
    });

    const unsubscribeSessionExpired = authBroadcaster.subscribe('session-expired', () => {
      console.log('[Auth] Session expired event received from another tab');
      // Clear local state and redirect
      queryClient.setQueryData(['/api/user'], null);
      clearAuthToken();
      toast({
        title: 'Session Expired',
        description: 'Your session has expired. Please log in again.',
        variant: 'destructive',
      });
      navigate('/auth');
    });

    return () => {
      unsubscribeLogin();
      unsubscribeLogout();
      unsubscribeSessionRefresh();
      unsubscribeSessionExpired();
    };
  }, [refetch, navigate, toast]);

  return (
    <AuthContext.Provider
      value={{
        user: user ?? null,
        isLoading,
        error,
        loginMutation,
        logoutMutation,
        registerMutation,
        getAuthToken,
        loginWithToken,
        requestPasswordResetMutation,
        resetPasswordMutation,
        resendVerificationMutation,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// No-op mutation stub for use when AuthProvider context is unavailable (e.g., during Vite HMR)
const noopMutation = { mutate: () => {}, mutateAsync: async () => {}, isPending: false, isError: false, isSuccess: false, isIdle: true, data: undefined, error: null, reset: () => {}, status: 'idle' as const, variables: undefined, failureCount: 0, failureReason: null, context: undefined, submittedAt: 0 } as any;

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    // Return safe defaults instead of throwing during HMR/hot reload
    // This prevents the entire app from crashing when Vite hot-updates a module
    console.warn('[useAuth] AuthProvider not found - returning defaults (likely HMR)');
    return {
      user: null,
      isLoading: true,
      error: null,
      loginMutation: noopMutation,
      logoutMutation: noopMutation,
      registerMutation: noopMutation,
      getAuthToken: async () => null,
      loginWithToken: async () => null,
      requestPasswordResetMutation: noopMutation,
      resetPasswordMutation: noopMutation,
      resendVerificationMutation: noopMutation,
    } as AuthContextType;
  }
  return context;
}
